import { AnyAction } from "redux";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
  createFailure,
  createSuccess,
  editFailure,
  editSuccess,
  listFailure,
  listRequest,
  listSuccess
} from "../actions/users";
import { UsersActionsTypes } from "../types/Users.reducer.types";

import { SagaCustomError } from "@/common/types";
import { UsersService } from "@/core/libs/api";
import { messageBarTypes } from "@/core/libs/message-bar";

const abortController = new AbortController();
const { signal } = abortController;

function* createUser({ payload }: AnyAction) {
  const { values, setSubmitting, closePanel, isAdmin } = payload;
  const service = UsersService(undefined, isAdmin);

  setSubmitting(true);

  try {
    const response = yield call(service.create, {
      params: { body: { ...values } },
      signal
    });

    if (response.error) {
      const error: SagaCustomError = new Error();
      error.statusCode = response.error.status;
      error.message = response.error.data?.message || response.error.message;

      throw error;
    }

    yield put(
      createSuccess({
        message: `Usuário criado com sucesso.`,
        type: messageBarTypes.SUCCESS
      })
    );

    if (closePanel) {
      setTimeout(() => {
        closePanel();
      }, 1000);
    }

    // REFRESH LIST
    yield put(listRequest(undefined, { isAdmin }));
  } catch (error) {
    yield put(
      createFailure({
        code: error?.statusCode,
        message:
          error?.message ||
          "Não foi possível criar o usuário com os dados informados.",
        type: messageBarTypes.ERROR
      })
    );
  }

  setSubmitting(false);
}

function* editUser({ payload }: AnyAction) {
  const { values, setSubmitting, closePanel, isAdmin } = payload;
  const service = UsersService(undefined, isAdmin);

  setSubmitting(true);

  try {
    const response = yield call(service.edit, {
      params: { body: { ...values } },
      signal
    });

    if (response.error) {
      const error: SagaCustomError = new Error();
      error.statusCode = response.error.status;
      error.message = response.error.data?.message || response.error.message;

      throw error;
    }

    yield put(
      editSuccess({
        message: `Usuário editado com sucesso.`,
        type: messageBarTypes.SUCCESS
      })
    );

    if (closePanel) {
      setTimeout(() => {
        closePanel();
      }, 1000);
    }

    // REFRESH LIST
    yield put(listRequest(undefined, { isAdmin }));
  } catch (error) {
    yield put(
      editFailure({
        code: error?.statusCode,
        message:
          error?.message ||
          "Não foi possível editar o usuário com os dados informados.",
        type: messageBarTypes.ERROR
      })
    );
  }

  setSubmitting(false);
}

function* listUsers({ payload }: AnyAction) {
  const { filter, isAdmin } = payload;
  const service = UsersService(undefined, isAdmin);

  try {
    const params = { query: { page: 1, limit: 10 } };

    if (filter) {
      params.query = { ...params.query, ...filter };
    }

    const response = yield call(service.list, { params, signal });

    if (response.error) {
      throw new Error();
    }

    yield put(listSuccess({ list: response.data }));
  } catch (error) {
    yield put(listFailure());
  }
}

export default all([
  takeLatest(UsersActionsTypes.CREATE_REQUEST, createUser),
  takeLatest(UsersActionsTypes.EDIT_REQUEST, editUser),
  takeLatest(UsersActionsTypes.LIST_REQUEST, listUsers)
]);
